import type { API, BlockAPI, BlockTune } from "@editorjs/editorjs";

import copyLinkIconUrl from "../../assets/icons/copyLinkIcon.svg";
import type { NewsMediaAsset, NewsVideoAsset } from "../../services/newsService/newsService.types";
import { getNewsFileDisplayName, getNewsFileExtension } from "./newsFilePresentation";


export class DuplicateBlockTune implements BlockTune {
  static get isTune() {
    return true;
  }

  private api: API;
  private block: BlockAPI;

  constructor({ api, block }: { api: API; block: BlockAPI }) {
    this.api = api;
    this.block = block;
  }

  render() {
    return {
      icon:
        '<img class="news-editor-duplicate-icon" src="' +
        copyLinkIconUrl +
        '" alt="" aria-hidden="true" />',
      title: "Дублировать блок",
      onActivate: () => this.duplicate(),
    };
  }

  private async duplicate() {
    const saved = await this.block.save();
    if (!saved) return;

    const index = this.api.blocks.getBlockIndex(this.block.id);
    if (index < 0) return;

    this.api.blocks.insert(
      saved.tool,
      saved.data,
      undefined,
      index + 1,
      true,
    );
  }
}

type MediaKind = "image" | "audio" | "file";

type MediaToolConfig = {
  uploadMedia: (file: File) => Promise<NewsMediaAsset>;
};

type VideoToolConfig = {
  uploadVideo: (file: File) => Promise<NewsVideoAsset>;
};

class BaseInlineStyleTool {
  protected command = "";
  protected label = "";
  protected button: HTMLButtonElement | null = null;

  static get isInline() {
    return true;
  }

  render() {
    const button = document.createElement("button");
    button.type = "button";
    button.classList.add("ce-inline-tool", "news-inline-tool");
    button.textContent = this.label;
    this.button = button;
    return button;
  }

  surround() {
    document.execCommand(this.command, false);
  }

  checkState() {
    if (!this.button) return;
    this.button.classList.toggle("ce-inline-tool--active", document.queryCommandState(this.command));
  }
}

export class UnderlineInlineTool extends BaseInlineStyleTool {
  protected command = "underline";
  protected label = "U";

  static get title() {
    return "Подчёркивание";
  }
}

export class StrikeInlineTool extends BaseInlineStyleTool {
  protected command = "strikeThrough";
  protected label = "S";

  static get title() {
    return "Зачёркивание";
  }
}

type DelimiterVariant = "line" | "space" | "stars";

export class NewsDelimiterTool {
  private data: { variant: DelimiterVariant };
  private api: any;
  private wrapper: HTMLDivElement | null = null;

  constructor({ data, api }: { data: Record<string, any>; api: any }) {
    const variant = data?.variant;
    this.data = {
      variant: variant === "space" || variant === "stars" ? variant : "line",
    };
    this.api = api;
  }

  static get isReadOnlySupported() {
    return true;
  }

  static get toolbox() {
    return {
      title: "Разделитель",
      icon: '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2 9H16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
    };
  }

  render() {
    this.wrapper = document.createElement("div");
    this.wrapper.className = "news-editor-delimiter";
    this.paint();
    return this.wrapper;
  }

  renderSettings() {
    const variants: Array<{ variant: DelimiterVariant; label: string; icon: string }> = [
      {
        variant: "line",
        label: "Линия",
        icon: '<svg width="18" height="18" viewBox="0 0 18 18"><path d="M2 9H16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
      },
      {
        variant: "space",
        label: "Пустой отступ",
        icon: '<svg width="18" height="18" viewBox="0 0 18 18"><path d="M5 4V14M13 4V14M3 6L5 4L7 6M11 12L13 14L15 12" stroke="currentColor" stroke-width="1.4" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      },
      {
        variant: "stars",
        label: "Три звёздочки",
        icon: '<svg width="18" height="18" viewBox="0 0 18 18"><circle cx="4" cy="9" r="1.2" fill="currentColor"/><circle cx="9" cy="9" r="1.2" fill="currentColor"/><circle cx="14" cy="9" r="1.2" fill="currentColor"/></svg>',
      },
    ];

    return variants.map((item) => ({
      label: item.label,
      icon: item.icon,
      isActive: this.data.variant === item.variant,
      closeOnActivate: true,
      onActivate: () => {
        this.data.variant = item.variant;
        this.paint();
        if (this.api?.blocks?.getCurrentBlockIndex) {
          this.api.blocks.getCurrentBlockIndex();
        }
      },
    }));
  }

  private paint() {
    if (!this.wrapper) return;
    this.wrapper.className = "news-editor-delimiter news-editor-delimiter--" + this.data.variant;
    this.wrapper.innerHTML = "";

    if (this.data.variant === "line") {
      const line = document.createElement("span");
      line.className = "news-editor-delimiter-line";
      this.wrapper.appendChild(line);
      return;
    }

    if (this.data.variant === "stars") {
      const stars = document.createElement("span");
      stars.className = "news-editor-delimiter-stars";
      stars.textContent = "***";
      this.wrapper.appendChild(stars);
      return;
    }

    const hint = document.createElement("span");
    hint.className = "news-editor-delimiter-space-label";
    hint.textContent = "Пустой отступ";
    this.wrapper.appendChild(hint);
  }

  save() {
    return { variant: this.data.variant };
  }
}

export class NewsMediaTool {
  private data: Record<string, any>;
  private config: MediaToolConfig;
  private wrapper: HTMLDivElement | null = null;

  constructor({ data, config }: { data: Record<string, any>; config: MediaToolConfig }) {
    this.data = data || {};
    this.config = config;
  }

  static get toolbox() {
    return {
      title: "Медиа / файл",
      icon: "<svg width=\"18\" height=\"18\" viewBox=\"0 0 18 18\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\"><rect x=\"2\" y=\"3\" width=\"14\" height=\"12\" rx=\"2\" stroke=\"currentColor\" stroke-width=\"1.5\"/><circle cx=\"6\" cy=\"7\" r=\"1.4\" fill=\"currentColor\"/><path d=\"M3.5 13L7.5 9.5L10 11.5L12.5 9L15 11.5\" stroke=\"currentColor\" stroke-width=\"1.5\" stroke-linecap=\"round\" stroke-linejoin=\"round\"/></svg>",
    };
  }

  render() {
    this.wrapper = document.createElement("div");
    this.wrapper.className = "news-editor-media-tool";
    this.paint();
    return this.wrapper;
  }

  private paint() {
    if (!this.wrapper) return;
    this.wrapper.innerHTML = "";

    if (!this.data.mediaId) {
      const title = document.createElement("div");
      title.className = "news-editor-tool-title";
      title.textContent = "Добавьте изображение, аудио или документ";
      this.wrapper.appendChild(title);

      const actions = document.createElement("div");
      actions.className = "news-editor-tool-actions";
      [
        { kind: "image" as MediaKind, label: "Изображение", accept: "image/*" },
        { kind: "audio" as MediaKind, label: "Аудио", accept: "audio/*,.mp3,.wav,.m4a,.ogg" },
        { kind: "file" as MediaKind, label: "Документ", accept: ".pdf,.doc,.docx,.txt,.xls,.xlsx" },
      ].forEach((option) => {
        const button = document.createElement("button");
        button.type = "button";
        button.textContent = option.label;
        button.addEventListener("click", () => this.chooseFile(option.kind, option.accept));
        actions.appendChild(button);
      });
      this.wrapper.appendChild(actions);
      return;
    }

    const meta = document.createElement("div");
    meta.className = "news-editor-media-meta";

    const kind = (this.data.kind || "file") as MediaKind;
    const accept = kind === "image"
      ? "image/*"
      : kind === "audio"
        ? "audio/*,.mp3,.wav,.m4a,.ogg"
        : ".pdf,.doc,.docx,.txt,.xls,.xlsx";

    if (kind === "image" && this.data.url) {
      const preview = document.createElement("div");
      preview.className = "news-editor-media-preview-frame";

      const image = document.createElement("img");
      image.src = this.data.url;
      image.alt = this.data.caption || this.data.name || "";
      image.className = "news-editor-media-preview";
      preview.appendChild(image);

      const replaceOverlay = document.createElement("button");
      replaceOverlay.type = "button";
      replaceOverlay.className = "news-editor-media-overlay-action";
      replaceOverlay.textContent = "Заменить";
      replaceOverlay.setAttribute("aria-label", "Заменить изображение");
      replaceOverlay.addEventListener("click", () => this.chooseFile(kind, accept));
      preview.appendChild(replaceOverlay);
      meta.appendChild(preview);
    } else if (kind === "audio" && this.data.url) {
      const audio = document.createElement("audio");
      audio.controls = true;
      audio.src = this.data.url;
      meta.appendChild(audio);

      const name = document.createElement("strong");
      name.textContent = this.data.name || "Аудиофайл";
      meta.appendChild(name);
    } else {
      const fileCard = document.createElement("div");
      fileCard.className = "news-editor-file-card";

      const extension = document.createElement("span");
      extension.className = "news-editor-file-extension";
      extension.textContent = getNewsFileExtension(this.data.name, this.data.mime);
      extension.setAttribute("aria-hidden", "true");
      fileCard.appendChild(extension);

      const info = document.createElement("div");
      info.className = "news-editor-file-info";
      const name = document.createElement("strong");
      name.textContent = getNewsFileDisplayName(this.data.name);
      info.appendChild(name);
      const hint = document.createElement("span");
      hint.textContent = "Документ";
      info.appendChild(hint);
      fileCard.appendChild(info);
      meta.appendChild(fileCard);
    }
    this.wrapper.appendChild(meta);

    const caption = document.createElement("input");
    caption.type = "text";
    caption.className = "news-editor-caption";
    caption.placeholder = "Подпись к медиа";
    caption.value = this.data.caption || "";
    caption.addEventListener("input", () => {
      this.data.caption = caption.value;
    });
    this.wrapper.appendChild(caption);

    if (kind !== "image") {
      const replaceButton = document.createElement("button");
      replaceButton.type = "button";
      replaceButton.className = "news-editor-replace-button";
      replaceButton.textContent = kind === "audio" ? "Заменить аудио" : "Заменить файл";
      replaceButton.addEventListener("click", () => this.chooseFile(kind, accept));
      this.wrapper.appendChild(replaceButton);
    }
  }

  private chooseFile(kind: MediaKind, accept: string) {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.onchange = async () => {
      const file = input.files && input.files[0];
      if (!file) return;
      if (this.wrapper) this.wrapper.dataset.uploading = "true";
      try {
        const asset = await this.config.uploadMedia(file);
        this.data = {
          kind,
          mediaId: asset.id,
          name: asset.name,
          mime: asset.mime,
          url: asset.url,
          caption: this.data.caption || "",
        };
        this.paint();
      } finally {
        if (this.wrapper) delete this.wrapper.dataset.uploading;
      }
    };
    input.click();
  }

  save() {
    return this.data;
  }

  validate(savedData: Record<string, any>) {
    return Boolean(savedData && savedData.mediaId && savedData.kind);
  }
}

export class NewsVideoTool {
  private data: Record<string, any>;
  private config: VideoToolConfig;
  private wrapper: HTMLDivElement | null = null;

  constructor({ data, config }: { data: Record<string, any>; config: VideoToolConfig }) {
    this.data = data || {};
    this.config = config;
  }

  static get toolbox() {
    return {
      title: "Видео",
      icon: "<svg width=\"18\" height=\"18\" viewBox=\"0 0 18 18\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\"><rect x=\"2\" y=\"3\" width=\"10\" height=\"12\" rx=\"2\" stroke=\"currentColor\" stroke-width=\"1.5\"/><path d=\"M12 7L16 5V13L12 11V7Z\" stroke=\"currentColor\" stroke-width=\"1.5\" stroke-linejoin=\"round\"/></svg>",
    };
  }

  render() {
    this.wrapper = document.createElement("div");
    this.wrapper.className = "news-editor-video-tool";
    this.paint();
    return this.wrapper;
  }

  private paint() {
    if (!this.wrapper) return;
    this.wrapper.innerHTML = "";

    const title = document.createElement("div");
    title.className = "news-editor-tool-title";
    title.textContent = this.data.videoId ? (this.data.name || "Видео") : "Загрузите видео";
    this.wrapper.appendChild(title);

    if (this.data.videoId) {
      const status = document.createElement("div");
      status.className = "news-editor-video-status";
      const state = this.data.status === "ready"
        ? "Готово"
        : this.data.status === "failed"
          ? "Ошибка обработки"
          : "Обрабатывается" + (this.data.progressPercent ? " " + this.data.progressPercent + "%" : "");
      status.textContent = state;
      this.wrapper.appendChild(status);

      const caption = document.createElement("input");
      caption.type = "text";
      caption.className = "news-editor-caption";
      caption.placeholder = "Подпись к видео";
      caption.value = this.data.caption || "";
      caption.addEventListener("input", () => {
        this.data.caption = caption.value;
      });
      this.wrapper.appendChild(caption);
    }

    const button = document.createElement("button");
    button.type = "button";
    button.className = "news-editor-replace-button";
    button.textContent = this.data.videoId ? "Заменить видео" : "Выбрать видео";
    button.addEventListener("click", () => this.chooseVideo());
    this.wrapper.appendChild(button);
  }

  private chooseVideo() {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "video/*,.mp4,.mov,.avi";
    input.onchange = async () => {
      const file = input.files && input.files[0];
      if (!file) return;
      if (this.wrapper) this.wrapper.dataset.uploading = "true";
      try {
        const asset = await this.config.uploadVideo(file);
        this.data = {
          videoId: asset.id,
          name: asset.name,
          status: asset.status,
          progressPercent: asset.progress_percent,
          playlistUrl: asset.playlist_url,
          caption: this.data.caption || "",
        };
        this.paint();
      } finally {
        if (this.wrapper) delete this.wrapper.dataset.uploading;
      }
    };
    input.click();
  }

  save() {
    return this.data;
  }

  validate(savedData: Record<string, any>) {
    return Boolean(savedData && savedData.videoId);
  }
}

export class NewsLinkTool {
  private data: Record<string, any>;

  constructor({ data }: { data: Record<string, any> }) {
    this.data = data || {};
  }

  static get toolbox() {
    return {
      title: "Внешняя ссылка",
      icon: "<svg width=\"18\" height=\"18\" viewBox=\"0 0 18 18\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M7 11L11 7M6 5L7 4C8.66 2.34 11.34 2.34 13 4C14.66 5.66 14.66 8.34 13 10L12 11M12 13L11 14C9.34 15.66 6.66 15.66 5 14C3.34 12.34 3.34 9.66 5 8L6 7\" stroke=\"currentColor\" stroke-width=\"1.5\" stroke-linecap=\"round\"/></svg>",
    };
  }

  render() {
    const wrapper = document.createElement("div");
    wrapper.className = "news-editor-link-tool";

    [
      { key: "url", placeholder: "https://example.com" },
      { key: "title", placeholder: "Название ссылки" },
      { key: "description", placeholder: "Краткое описание" },
    ].forEach((field) => {
      const input = document.createElement("input");
      input.type = field.key === "url" ? "url" : "text";
      input.placeholder = field.placeholder;
      input.value = this.data[field.key] || "";
      input.addEventListener("input", () => {
        this.data[field.key] = input.value;
      });
      wrapper.appendChild(input);
    });
    return wrapper;
  }

  save() {
    return this.data;
  }

  validate(savedData: Record<string, any>) {
    const url = String((savedData && savedData.url) || "");
    return url.startsWith("http://") || url.startsWith("https://");
  }
}
